import s from './style.module.scss';
import { Display } from '@/components/Display/Display';
import DetailModal from './DetailModal';
import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import useMultimodalReference from '@/pages/hooks/Multimodal/useMultimodalReference';
import useMultimodalCardGeneration from '@/pages/hooks/Multimodal/useMultimodalCardGeneration';
import useMultimodalDetailModal from '@/pages/hooks/Multimodal/useMultimodalDetailModal';
import MultimodalCard from './MultimodalCard';
import grtStore from '@/store/grtStore';
import { observer } from 'mobx-react-lite';

const WebMultimodalLearning = (props) => {
    const { data = [], loading = false, description = '', reference_id, task_list = [] } = props.multimodal_learning_result || {};
    const { agentName } = props;
    const hasVideoData = Array.isArray(data) && data.length > 0;
    // 使用全局 store 的 multimodalStore 读取/写入面板状态
    const { multimodalStore } = grtStore;
    const { setMultimodalReference } = multimodalStore;
    const detailPanelOpen = multimodalStore?.detailPanelOpen;
    const activeDetailMessageId = multimodalStore?.activeMessageId;
    const setDetailPanelOpen = multimodalStore?.setDetailPanelOpen;
    const setActiveDetailMessageId = multimodalStore?.setActiveMessageId;
    const { messageId } = props;
    // 使用 hook 获取工具方法（包括 setMultimodalReference）
    const { createVideoHandlers } = useMultimodalReference();

    // 创建视频处理方法的工厂函数
    const createVideoHandlersWithState = (setDetailDataFn, setShowVideoListFn, handleExpandClickFn) => {
        const handlers = createVideoHandlers({
            data,
            agentName,
            setDetailData: setDetailDataFn,
            setShowVideoList: setShowVideoListFn,
            handleBackToList: () => {
                setDetailDataFn({
                    title: '视频列表',
                    type: 'video_list',
                    description: ''
                });
                setShowVideoListFn(true);
            }
        });

        const handleContinueAsk = () => {
            const videoList = Array.isArray(data) && data.length > 0 ? data : [];
            const firstVideoTitle = videoList[0]?.title || '';
            const videoCount = videoList.length;

            if (setMultimodalReference) {
                const referenceData = {
                    title: '多模态学习',
                    description: description || '根据你的问题，我为你寻找到相关视频，并基于这些视频生成多模态学习的内容',
                    videoCount,
                    firstVideoTitle,
                    reference_id,
                    onListClick: handleExpandClickFn
                };
                console.log('referenceData', referenceData);
                setMultimodalReference(referenceData);
            }
        };

        return {
            ...handlers,
            handleContinueAsk
        };
    };

    // 使用详情 Modal hook
    const { detailData, setDetailData, setShowVideoList, openDetailModal, handleExpandClick, handleClose, isCurrentDrawerOpen } =
        useMultimodalDetailModal({
            messageId,
            data,
            detailPanelOpen,
            activeDetailMessageId,
            setDetailPanelOpen,
            setActiveDetailMessageId,
            createVideoHandlersFn: createVideoHandlersWithState
        });

    // 使用卡片生成 hook
    const { getCurrentCardStatus, handleItemClick, handleRetry } = useMultimodalCardGeneration({
        messageId,
        reference_id,
        task_list,
        detailPanelOpen,
        activeDetailMessageId,
        onOpenDetailModal: openDetailModal
    });

    // 点击引用图标
    const handleReferenceClick = () => {
        const videoList = Array.isArray(data) && data.length > 0 ? data : [];
        const firstVideoTitle = videoList[0]?.title || '';
        const videoCount = videoList.length;

        if (setMultimodalReference) {
            const referenceData = {
                videoReferences: data,
                title: '多模态学习',
                description: description || '根据你的问题，我为你寻找到相关视频，并基于这些视频生成多模态学习的内容',
                videoCount,
                firstVideoTitle,
                reference_id,
                onListClick: handleExpandClick
            };
            setMultimodalReference(referenceData);
        }
    };

    // 卡片配置
    const cardConfigs = [
        {
            title: '文字总结',
            description: '精炼核心,快速抓住重点信息',
            type: 'text_summary'
        },
        {
            title: '思维导图',
            description: '可视化知识结构,让思路一目了然',
            type: 'mind_map'
        },
        {
            title: '播客',
            description: '听觉化知识,填充你的碎片时间',
            type: 'video_player'
        }
    ];

    // 没有视频数据时，如果当前有已打开的详情，保持组件挂载
    if (!hasVideoData && !detailData) {
        return null;
    }

    const currentCardStatus = getCurrentCardStatus();
    return (
        <div className={`grt-multimodal-card ${s['multimodal-container']}`} style={{ padding: '12px 8px' }}>
            <div className={s['video-count-box']} onClick={handleExpandClick} style={{ cursor: 'pointer' }}>
                <span className={s['text']}>{description}</span>
                <div className={s['icons']}>
                    <Tooltip title="引用提问">
                        <span
                            className={s['refresh-icon']}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleReferenceClick();
                            }}
                        >
                            <i className="iconfont icon-yinyong" style={{ fontSize: '12px', color: '#8C8C8C' }}></i>
                        </span>
                    </Tooltip>
                    <Tooltip title={isCurrentDrawerOpen ? '收起' : '查看全部'}>
                        <span
                            className={s['expand-icon']}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (isCurrentDrawerOpen) {
                                    handleClose();
                                } else {
                                    handleExpandClick();
                                }
                            }}
                        >
                            <i
                                className={`iconfont ${isCurrentDrawerOpen ? 'icon-shouqi' : 'icon-zhankai'}`}
                                style={{ fontSize: '16px', color: '#8C8C8C' }}
                            ></i>
                        </span>
                    </Tooltip>
                </div>
            </div>
            <div className={s['description-box']} onClick={handleExpandClick} style={{ cursor: 'pointer' }}>
                {'根据你的问题，我为你寻找到相关视频，并基于这些视频生成多模态学习的内容，如下。'}
            </div>
            <div className={s['box-wrap']}>
                <Display show={!loading}>
                    <div className={s['list-wrap']}>
                        {cardConfigs.map((cardInfo) => (
                            <MultimodalCard
                                key={cardInfo.type}
                                cardInfo={cardInfo}
                                status={currentCardStatus[cardInfo.type]}
                                onClick={handleItemClick}
                                onRetry={handleRetry}
                            />
                        ))}
                    </div>
                </Display>
            </div>
        </div>
    );
};

export default observer(WebMultimodalLearning);
